import {Badge, Button, Form} from 'react-bootstrap'
import {FormattedMessage} from 'react-intl'
import {TFormAllValues} from '../../../policies/policiesTypes'
import {FormikErrors, FormikTouched} from 'formik'
import {useSearchParams} from 'react-router-dom'
import {MembersEdit} from './MembersEdit'
import { INIT_FORM_MEMBERS } from '../../../../helpers/consts'
import {isLifeKind, TPolicyFormKind} from '../../../../helpers/policyFormKind'

interface IFamiliMemsProps {
  onPrevStep: () => void
  onNextStep: () => void
  handleChange: (field: string, value: string | boolean) => void
  handleBlur: (field: string) => void
  addMember: () => void
  removeMember: (index: number) => void
  allValues: TFormAllValues
  errors: FormikErrors<TFormAllValues>
  touched: FormikTouched<TFormAllValues>
  loadingCreate: boolean
  isDisableEdit?: boolean
  onSwitchWithHolder: (index: number) => void   // 👈 NUEVO
  canSwitchHolder: boolean
  formKind?: TPolicyFormKind
}

export const FamilyMembersTab: React.FC<IFamiliMemsProps> = ({
  onPrevStep,
  onNextStep,
  allValues,
  errors,
  touched,
  handleChange,
  handleBlur,
  addMember,
  removeMember,
  loadingCreate,
  isDisableEdit = false,
  onSwitchWithHolder,
  canSwitchHolder,
  formKind = 'GENERIC',
}) => {
  const [searchParams] = useSearchParams()
  const lead = searchParams.get('lead')
  const isLife = isLifeKind(formKind)

  // Suma en vivo por grupo: el guardado se bloquea si algún grupo no da 100%.
  const percentageTotals = (allValues.policyMembers || []).reduce(
    (acc, member) => {
      if (!member?.isBeneficiary) return acc
      const group = (member.beneficiaryClass || 'PRIMARY') as 'PRIMARY' | 'CONTINGENT'
      acc[group] += Number(member.beneficiaryPercentage) || 0
      return acc
    },
    {PRIMARY: 0, CONTINGENT: 0}
  )

  const renderTotal = (group: 'PRIMARY' | 'CONTINGENT') => {
    const total = percentageTotals[group]
    if (!total) return null
    const ok = Math.abs(total - 100) < 0.005
    // text-white explícito: Metronic pisa el color de texto por defecto de los badges
    // de Bootstrap y sobre rojo/verde queda negro, sin contraste.
    return (
      <Badge bg={ok ? 'success' : 'danger'} className='ms-2 text-white'>
        {group === 'PRIMARY' ? (
          <FormattedMessage
            id='policies.detail.form.life.primaryTotal'
            defaultMessage='Primary: {total}%'
            values={{total}}
          />
        ) : (
          <FormattedMessage
            id='policies.detail.form.life.contingentTotal'
            defaultMessage='Contingent: {total}%'
            values={{total}}
          />
        )}
      </Badge>
    )
  }

  return (
    <div className='family-members-container'>
      <div className='kt-heading kt-heading--md custom-border-brand'>
        {isLife ? (
          <>
            <FormattedMessage
              id='policies.detail.form.life.beneficiariesTitle'
              defaultMessage='Beneficiaries'
            />
            {renderTotal('PRIMARY')}
            {renderTotal('CONTINGENT')}
          </>
        ) : (
          <FormattedMessage
            id='policies.list.wizard.family.title'
            defaultMessage='Applicants & Dependents'
          />
        )}
      </div>
      {isLife && (
        <div className='alert alert-light-primary mt-4 mb-0 py-2'>
          <small>
            <FormattedMessage
              id='policies.detail.form.life.beneficiariesHint'
              defaultMessage='Family members are not beneficiaries automatically — mark each one explicitly. Primary beneficiaries must add up to 100%, and contingent beneficiaries too if any are registered.'
            />
          </small>
        </div>
      )}
      {lead && Number(lead) ? (
        <div className='mt-6'>
          <Form.Group>
            <Form.Label>
              <FormattedMessage
                id='policies.list.wizard.holder.findContact'
                defaultMessage='Find and select a contact'
              />
            </Form.Label>
            <Form.Text style={{fontSize: '12px'}}>
              <FormattedMessage
                id='policies.list.wizard.holder.searchHelp'
                defaultMessage='Search by name, email address, phone number or last 4 digits of SSN'
              />
            </Form.Text>
          </Form.Group>
        </div>
      ) : (
        <>
          <div className='list-member'>
            {allValues.policyMembers && allValues.policyMembers.length > 0 && (
              <div>
                <h4 className='mb-5 mt-5'>
                  {isLife ? (
                    <FormattedMessage
                      id='policies.detail.form.life.beneficiariesCount'
                      defaultMessage='Beneficiaries ({count})'
                      values={{
                        count: allValues.policyMembers.filter((m) => m?.isBeneficiary).length,
                      }}
                    />
                  ) : (
                    <FormattedMessage
                      id='policies.detail.form.member.membersCount'
                      defaultMessage='Members ({count})'
                      values={{count: allValues.policyMembers.length}}
                    />
                  )}
                </h4>
                {allValues.policyMembers.map((value, index) => {
                  // console.log('Renderizando miembro:', index, value)
                  return (
                    <MembersEdit
                      key={value.id || `new-${index}`}
                      membernum={index}
                      allValues={allValues}
                      valuesMembers={value}
                      errors={
                        (errors.policyMembers?.[index] ?? {}) as FormikErrors<
                          typeof INIT_FORM_MEMBERS
                        >
                      }
                      touched={
                        (touched.policyMembers?.[index] ?? {}) as FormikTouched<
                          typeof INIT_FORM_MEMBERS
                        >
                      }
                      handleChange={(field, value) =>
                        handleChange(`policyMembers[${index}].${field}`, value)
                      }
                      handleBlur={(field) => handleBlur(`policyMembers[${index}].${field}`)}
                      // handleChange={handleChange}
                      removeMember={removeMember}
                      onSwitchWithHolder={onSwitchWithHolder}
                      canSwitchHolder={canSwitchHolder}
                      formKind={formKind}
                    />
                  )
                })}
              </div>
            )}
          </div>
          <div className='mt-5 d-flex align-items-center justify-content-center'>
            <span
              style={{
                width: '100%',
                height: '2px',
                flex: 1,
                background: '#c7c7c7',
                display: 'inline-block',
              }}
            />
            <h5 className='ms-3 me-3 mt-0 mb-0' style={{fontSize: '14px'}}>
              {isLife ? (
                <FormattedMessage
                  id='policies.detail.form.life.addBeneficiaryBelow'
                  defaultMessage='Add below the information of each Beneficiary'
                />
              ) : (
                <FormattedMessage
                  id='policies.detail.form.member.addBelow'
                  defaultMessage='Add below the information of each Member'
                />
              )}
            </h5>
            <span
              style={{
                width: '100%',
                height: '2px',
                flex: 1,
                background: '#c7c7c7',
                display: 'inline-block',
              }}
            />
          </div>
          <div className='mt-5 mb-5 pb-6'>
            <Button
              variant='success'
              size='sm'
              onClick={(e) => {
                e.preventDefault()
                addMember()
              }}
            >
              <i className='bi bi-plus me-1' />
              {isLife ? (
                <FormattedMessage
                  id='policies.detail.form.life.addBeneficiary'
                  defaultMessage='Add beneficiary'
                />
              ) : (
                <FormattedMessage
                  id='policies.list.wizard.family.addMember'
                  defaultMessage='Add member'
                />
              )}
            </Button>
          </div>
        </>
      )}
      <div className='btn-prev-next'>
        <Button size='sm' variant='secondary' onClick={onPrevStep}>
          <FormattedMessage id='policies.list.wizard.previous' defaultMessage='PREVIOUS' />
        </Button>
        <Button size='sm' onClick={onNextStep}>
          <FormattedMessage id='policies.list.wizard.nextStep' defaultMessage='NEXT STEP' />
        </Button>
      </div>
    </div>
  )
}
